import { useEffect, useRef } from "react";
import { useMeContext } from "../../MeApp";
import { useReportWrongNames } from "../../meHooks";
import { StepShell, StepHeading } from "@codel/dova-ds";
import { Button } from "../components/Button";
import type { StepProps } from "../flowConfig";

/**
 * S2b — "Neither of these is me", the dead end off the picker.
 *
 * THE COPY MAY ONLY PROMISE WHAT THE CODE ON THIS SCREEN ACTUALLY DOES. It used to say
 * "A fresh invite is on the way… there's nothing you need to do for now", and nothing
 * anywhere sent an invite: the component's only handlers closed the window or went back.
 * Someone told that had no reason to chase it and no way to find out they should.
 *
 * What happens now is one thing: arriving here posts `/me/couple/report-wrong-names`,
 * which raises a Slack alert carrying the couple and roster IDS for an operator. That is
 * a person being told, which is NOT a therapist reissuing an invite, so the copy hands
 * the client the honest next step (contact the therapist) rather than a wait.
 *
 * The report is deliberately invisible: no spinner, no error state, no gate on the copy.
 * It fires once on arrival and its outcome changes nothing on screen. A client who has
 * just told us our record of their name is wrong should not also be shown our plumbing
 * failing.
 */
export function S2bNeither({ onJump }: StepProps) {
  const { couple } = useMeContext();
  const therapistName = couple?.therapistName ?? "your therapist";
  const { mutate: reportWrongNames } = useReportWrongNames();

  // Once per arrival. The ref (not just the empty-ish deps) is what holds under React
  // StrictMode's double-invoked effects in dev; the server also dedups per couple, so a
  // reload cannot flood the channel either.
  const reported = useRef(false);
  useEffect(() => {
    if (reported.current) return;
    reported.current = true;
    reportWrongNames(undefined, {
      onError: (err) => {
        // Swallowed on purpose (see the docstring). Logged so it is not fully silent.
        console.warn("[me] wrong-names report failed:", err);
      },
    });
  }, [reportWrongNames]);

  const handleClose = () => {
    if (window.opener) {
      window.close();
    } else {
      window.history.back();
    }
  };

  return (
    <StepShell footer={<Button onClick={handleClose}>Close</Button>}>
      <div className="flex-1 flex flex-col items-center justify-center text-center gap-6">
        <StepHeading align="center" title="Thanks for flagging that.">
          <p className="max-w-[42ch]">
            The names on this link don't look right, so it has been flagged for
            review. The quickest fix is to contact {therapistName} directly and
            ask for a link with your own name on it.
          </p>
        </StepHeading>
        <button
          type="button"
          onClick={() => onJump("partner_picker")}
          className="text-xs text-[var(--cta-ink)] underline underline-offset-2"
        >
          Picked this by mistake? Go back
        </button>
      </div>
    </StepShell>
  );
}
